/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.21.0
 * @since 1.0.0
 */
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import {
    getPageInquiryInfoUrl,
    isPageInquiryInfoExternalUrl,
} from '@/main/pages/PageInquiryInfo/utils/getInquiryInfoUrl';
import { IRootReducer } from '@/main/reducers/rootReducer';
import { openExternalRoute } from '@/main/routes/constants/externalRoutes';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { Button, ExtensibleTextBlock, InfoBlock, LinkBlock } from '@components/common';
import { LayoutStyled } from '@components/common/Block/Layout';
import { EColorStatus } from '@modules/app/appConstants';
import { appHistoryPush } from '@modules/app/appHistory';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { logBusinessEvent } from '@modules/logging/loggingActions';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging/loggingConstants';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';
import { guardComponentType } from '@modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '@modules/role/selectors/guardSelector';

import { FooterModalStyled, FooterModalTextStyled, FooterModalTitleStyled } from '../../styled/footerModalStyled';


/**
 * @description Inquiry Created Modal component props
 * @interface IInquiryCreatedModalProps
 */
export interface IInquiryCreatedModalProps {
    fileAttachedWithErrors: boolean;
    inquiryNumber: string;
    showInqLink: boolean;
    onCloseModal: () => void;
}

type TInquiryCreatedModal = IInquiryCreatedModalProps & IInquiryCreatedModalConnectedProps & IDispatchProps;

const InquiryCreatedModal = (props: TInquiryCreatedModal) => {
    const { createInquiry: translation } = props.translation;

    const onInquiryClickHandler = (): void => {
        props.logBusinessEvent({
            eventName: ELogBusinessEventName.USER_SHOW_INQUIRY_INFO,
            type: ELogBusinessEventType.common,
        });
        const url = getPageInquiryInfoUrl(props.componentsFeature, props.inquiryNumber);
        if (isPageInquiryInfoExternalUrl(props.componentsFeature)) {
            openExternalRoute(url);
        } else {
            appHistoryPush(url);
        }
        props.onCloseModal();
    };

    const onInquiryWheelClickHandler = (): void => {
        openExternalRoute(getPageInquiryInfoUrl(props.componentsFeature, props.inquiryNumber), '_blank');
    };

    const onInquiryHistoryClickHandler = (): void => {
        props.logBusinessEvent({
            eventName: ELogBusinessEventName.USER_INQUIRIES,
            type: ELogBusinessEventType.common,
        });
        appHistoryPush(RoutePath.inquiries);
        props.onCloseModal();
    };

    return (
        <FooterModalStyled
            className="inquiry-created-modal"
            data-modal-attention="attention"
        >
            <FooterModalTitleStyled
                className="inquiry-created-modal__title mb-3"
            >
                {translation.inquirySend}
            </FooterModalTitleStyled>
            <FooterModalTextStyled
                className="inquiry-created-modal__text mb-5"
            >
                {
                    props.showInqLink &&
                    <ExtensibleTextBlock
                        inserts={[
                            <LinkBlock
                                key={props.inquiryNumber}
                                onClick={onInquiryClickHandler}
                                onWheelClick={onInquiryWheelClickHandler}
                            >
                                {props.inquiryNumber}
                            </LinkBlock>,
                        ]}
                        text={translation.inquiryCreatedLink}
                    />
                }
                <ExtensibleTextBlock
                    inserts={[
                        <LinkBlock
                            key={translation.inquiryHistory}
                            onClick={onInquiryHistoryClickHandler}
                            onWheelClick={onInquiryWheelClickHandler}
                        >
                            {translation.inquiryHistory}
                        </LinkBlock>,
                    ]}
                    text={translation.inquiryCreatedList}
                />
            </FooterModalTextStyled>
            {
                props.fileAttachedWithErrors && (
                    <LayoutStyled className="mb-5">
                        <InfoBlock colorStatus={EColorStatus.WARNING}>
                            {translation.fileAttachedWithErrors}
                        </InfoBlock>
                    </LayoutStyled>
                )
            }
            <Button
                data-modal-attention="close-button"
                kind="medium"
                onClick={props.onCloseModal}
            >
                {props.translation.createInquiry.close}
            </Button>
        </FooterModalStyled>
    );
};

export interface IInquiryCreatedModalConnectedProps {
    componentsFeature: guardComponentType;
    translation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IInquiryCreatedModalConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    translation: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InquiryCreatedModal);
